'use client';

import React, { useState, useEffect, ReactNode } from 'react';
import { Header } from '@/components/ui/home/header';
import { BottomNavbar } from '@/components/ui/home/footer';

export interface BreadcrumbItem {
  label: string;
  href?: string;
  isActive?: boolean;
}

interface SubInfoProps {
  pageKey?: string;
  [key: string]: any;
}

interface FixedLayoutProps {
  children: ReactNode;
  showBackToArtworks?: boolean;
  hideUpcoming?: boolean;
  hideVisions?: boolean;
  breadcrumbs?: BreadcrumbItem[];
  subInfo?: SubInfoProps;
  scrollOn?: boolean;
}

export function FixedLayout({
  children,
  hideUpcoming = false,
  hideVisions = true, // æ
  breadcrumbs = [],
  subInfo,
  scrollOn = true,
}: FixedLayoutProps) {
  const [isCatalogMenuOpen, setIsCatalogMenuOpen] = useState(false);
  const [isLeftMenuOpen, setIsLeftMenuOpen] = useState(false);

  const handleCatalogMenuToggle = () => {
    setIsLeftMenuOpen(false);
    setIsCatalogMenuOpen(!isCatalogMenuOpen);
  };

  const handleLeftMenuToggle = () => {
    setIsCatalogMenuOpen(false);
    setIsLeftMenuOpen(!isLeftMenuOpen);
  };

  // Close menu when pressing Escape
  useEffect(() => {
    const body = document.getElementsByTagName('body')[0];
    if (scrollOn) {
      body.classList.remove('no-scroll');
    } else {
      body.classList.add('no-scroll');
    }
  }, [scrollOn]);

  // Close menu when pressing Escape
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && (isCatalogMenuOpen || isLeftMenuOpen)) {
        setIsCatalogMenuOpen(false);
        setIsLeftMenuOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isCatalogMenuOpen, isLeftMenuOpen]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isCatalogMenuOpen && !isLeftMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('[data-catalog-button]') && !target.closest('.catalog-mega-menu')) {
        setIsCatalogMenuOpen(false);
        setIsLeftMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isCatalogMenuOpen, isLeftMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-black">
      {/* Fixed Top Navigation */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-black shadow-md pointer-events-auto">
        {/* Ensure the header receives all pointer events */}
        <Header
          onCatalogMenuToggle={handleCatalogMenuToggle}
          isCatalogMenuOpen={isCatalogMenuOpen}
          onLeftMenuToggle={handleLeftMenuToggle}
          isLeftMenuOpen={isLeftMenuOpen}
          breadcrumbs={breadcrumbs}
        />
      </div>

      {/* No separate catalog mega menu - it's now integrated into the header */}

      {/* Main Content Area - White scrollable container with padding for fixed elements */}
      {/* border-zinc-200 border*/}
      <div className="flex-1">
        <div
          className={`fixedlayout bg-white text-black overflow-y-hidden mx-2 
          rounded-md shadow-lg transition-all duration-300 ease-in-out relative z-10
          ${isCatalogMenuOpen || isLeftMenuOpen ? 'catalog-menu-open' : ' '}
          `}
        >
          {children}
        </div>
      </div>

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-black shadow-md hidden lg:block">
        <BottomNavbar hideUpcoming={hideUpcoming} hideVisions={hideVisions} subInfo={subInfo} />
      </div>
    </div>
  );
}
